import { EditButton, ShowButton } from 'react-admin';
import React from 'react'
import {
  List,
  DatagridConfigurable,
  TextInput, FunctionField,
  ExportButton,
  SelectInput,
  TextField,
  DateField,
  useRecordContext,
} from 'react-admin';

import { Box, ButtonGroup, Chip, IconButton, Tooltip } from '@mui/material';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import EcgEnabledText from './EcgEnabledText';

const ActivationChip = () => {
  const record = useRecordContext();
  if (!record) return null;

  if (record.activated) {
    return <Chip label="Activated" color="success" size="small" />;
  }
  if (record.pending_invitation) {
    return <Chip label="Pending invitation" color="warning" size="small" />;
  }
  return <Chip label="Not activated" color="error" size="small" />;
};

// Email cell with a "login as" icon underneath (opens the front dashboard
// with the impersonation token already embedded in the URL)
const EmailWithLoginAs = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start">
      <span>{record.email}</span>
      {record.login_as_url && (
        <Tooltip title="Login as this user">
          <IconButton
            size="small"
            component="a"
            href={record.login_as_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label="Login as this user"
          >
            <SwitchAccountIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const ExternalServicesChips = () => {
  const record = useRecordContext();
  if (!record) return null;

  const services = record.connected_external_services || [];
  if (services.length === 0) return <span style={{ color: '#999' }}>—</span>;

  return (
    <Box display="flex" gap={0.5} flexWrap="wrap">
      {services.map((service) => (
        <Chip key={service} label={service} size="small" variant="outlined" color="primary" />
      ))}
    </Box>
  );
};

const LastLoginField = () => {
  const record = useRecordContext();
  if (!record) return null;

  if (!record.last_login_date) {
    return <span style={{ color: '#999' }}>Jamais connecté</span>;
  }
  return <DateField source="last_login_date" showTime />;
};

const UserList = (props) => {
  const filters = [
    <TextInput label="Search" source="q" resettable alwaysOn />,
    <SelectInput
      label="Subscription"
      source="subscriptionStatus"
      choices={[
        { id: 'paid', name: 'Paying subscribers' },
        { id: 'any', name: 'Any subscription record' },
        { id: 'none', name: 'No subscription' },
      ]}
      alwaysOn
    />,
  ];

  const actions = <ExportButton />;

  return (
    <List {...props} perPage={50} sort={{ field: 'id', order: 'DESC' }} filters={filters} actions={actions}>
      <DatagridConfigurable bulkActionButtons={false}>
        <TextField source="id" sortable={false} label="ID" />
        <TextField source="firstname" />
        <TextField source="lastname" />
        <FunctionField label="Email" render={() => <EmailWithLoginAs />} sortable={false} />
        <FunctionField label="Status" render={() => <ActivationChip />} sortable={false} />
        <FunctionField
          label="ECG Enabled"
          render={EcgEnabledText}
          sortable={false}
        />
        <FunctionField
          label="External accounts"
          render={() => <ExternalServicesChips />}
          sortable={false}
        />
        <TextField source="lang" label="Lang" sortable={false} />
        <FunctionField label="Last login" source="lastLoginDate" render={() => <LastLoginField />} sortable />
        <ButtonGroup>
          <ShowButton />
          <EditButton />
        </ButtonGroup>
      </DatagridConfigurable>
    </List>
  );
};

export default UserList;
